import { LoginFailedError, mapBackendError, PinRetryRequiredError } from './errors.js';
import type {
  ChallengeData,
  LoginOptions,
  LoginResult,
  LoginSessionEvent,
  WebSocketConstructor,
  WebSocketLike,
  WSCertificateSummary,
} from './types.js';
import {
  decodeWSEnvelope,
  encodeWSEnvelope,
  parseCertificateSummaries,
} from './ws-envelope.js';

type SocketEventTarget = WebSocketLike & {
  on?: (event: string, listener: (...args: unknown[]) => void) => void;
  addEventListener?: (event: string, listener: (...args: unknown[]) => void) => void;
};

function attachSocketHandlers(
  socket: SocketEventTarget,
  handlers: {
    open: () => void;
    message: (data: unknown) => void;
    error: () => void;
    close: (ev: { code?: number; reason?: string }) => void;
  },
): void {
  // Node `ws` package is an EventEmitter; browsers expose onopen/onmessage properties.
  if (typeof socket.on === 'function') {
    socket.on('open', handlers.open);
    socket.on('message', (data: unknown) => {
      if (typeof data === 'string') {
        handlers.message(data);
      } else if (data instanceof ArrayBuffer) {
        handlers.message(data);
      } else if (data && typeof data === 'object' && 'toString' in data) {
        handlers.message(String(data));
      } else {
        handlers.message(String(data));
      }
    });
    socket.on('error', handlers.error);
    socket.on('close', (code: unknown, reason: unknown) => {
      handlers.close({
        code: typeof code === 'number' ? code : undefined,
        reason: reason == null ? undefined : String(reason),
      });
    });
    return;
  }

  socket.onopen = () => handlers.open();
  socket.onmessage = (ev) => handlers.message(ev.data);
  socket.onerror = () => handlers.error();
  socket.onclose = (ev) => handlers.close(ev);
}

export interface LoginSessionParams {
  wsLoginUrl: string;
  challenge: ChallengeData;
  origin: string;
  options: LoginOptions;
  WebSocketImpl: WebSocketConstructor;
  createWebSocket?: (url: string) => WebSocketLike;
}

/**
 * Mode A WebSocket login state machine (`AGENT_WS_PROTOCOL.md`).
 *
 * Browser never sends/receives signature, certificate DER, or JWT on this channel.
 */
export function runLoginSession(params: LoginSessionParams): Promise<LoginResult> {
  const { wsLoginUrl, challenge, options, WebSocketImpl, createWebSocket } = params;
  const pinMode = options.pinMode ?? 'browser';
  const isAgentMode = pinMode === 'agent';

  return new Promise<LoginResult>((resolve, reject) => {
    let settled = false;
    let sessionId: string | null = null;
    let pinSubmitted = false;
    let currentPin = typeof options.pin === 'string' ? options.pin : '';
    let socket: WebSocketLike;

    const emit = (event: LoginSessionEvent) => {
      try {
        options.onEvent?.(event);
      } catch {
        // Listener errors must not break the login session.
      }
    };

    const settleReject = (error: Error) => {
      if (settled) {
        return;
      }
      settled = true;
      try {
        socket.close();
      } catch {
        // ignore
      }
      reject(error);
    };

    const settleResolve = (result: LoginResult) => {
      if (settled) {
        return;
      }
      settled = true;
      try {
        socket.close();
      } catch {
        // ignore
      }
      resolve(result);
    };

    const send = (type: string, payload: Record<string, unknown> = {}) => {
      socket.send(
        encodeWSEnvelope({
          type,
          sessionId,
          payload,
        }),
      );
    };

    const selectCertificateId = async (
      certificates: WSCertificateSummary[],
    ): Promise<string> => {
      if (options.certificateId) {
        const match = certificates.find((c) => c.certificateId === options.certificateId);
        if (!match) {
          throw new Error('certificateId not present in CERTIFICATE_LIST');
        }
        return match.certificateId;
      }
      if (options.onSelectCertificate) {
        return options.onSelectCertificate(certificates);
      }
      if (certificates.length === 1) {
        return certificates[0].certificateId;
      }
      if (certificates.length === 0) {
        throw mapBackendError({ code: 1501, message: 'no certificates available on token' });
      }
      throw new Error('multiple certificates found — specify certificateId or onSelectCertificate');
    };

    const resolvePin = async (reason?: string): Promise<string> => {
      if (!reason && currentPin.length > 0) {
        return currentPin;
      }
      if (!reason && options.onRequestPin) {
        const pin = await options.onRequestPin();
        if (typeof pin !== 'string' || pin.length === 0) {
          throw new Error('onRequestPin must return a non-empty PIN');
        }
        currentPin = pin;
        return currentPin;
      }
      if (options.onPinRetry) {
        const pin = await options.onPinRetry(reason ?? 'pin_required');
        if (typeof pin !== 'string' || pin.length === 0) {
          throw new Error('onPinRetry must return a non-empty PIN');
        }
        currentPin = pin;
        return currentPin;
      }
      if (reason) {
        throw new PinRetryRequiredError(reason);
      }
      throw new Error('pin is required, or provide onRequestPin / onPinRetry');
    };

    const handleMessage = async (raw: unknown) => {
      let envelope;
      try {
        envelope = decodeWSEnvelope(raw);
      } catch (error) {
        settleReject(error instanceof Error ? error : new Error('invalid WebSocket message'));
        return;
      }

      if (envelope.session_id) {
        sessionId = envelope.session_id;
      }

      const payload = envelope.payload;

      switch (envelope.type) {
        case 'READY': {
          emit({
            type: 'READY',
            challengeId: String(payload.challenge_id ?? challenge.challenge_id),
            pinMode: String(payload.pin_mode ?? 'browser'),
            expiresAt: Number(payload.expires_at ?? 0),
            sessionId: sessionId ?? '',
          });
          break;
        }
        case 'CERTIFICATE_LIST': {
          if (isAgentMode) {
            settleReject(
              new LoginFailedError(
                'protocol_invalid_state',
                'CERTIFICATE_LIST is not allowed for pin_mode=agent',
                { challengeId: challenge.challenge_id },
              ),
            );
            return;
          }
          const certificates = parseCertificateSummaries(payload);
          emit({
            type: 'CERTIFICATE_LIST',
            certificates,
            sessionId: sessionId ?? '',
          });
          try {
            const certificateId = await selectCertificateId(certificates);
            send('SELECT_CERTIFICATE', { certificate_id: certificateId });
          } catch (error) {
            settleReject(error instanceof Error ? error : new Error('certificate selection failed'));
          }
          break;
        }
        case 'CERTIFICATE_SELECTED': {
          if (isAgentMode) {
            break;
          }
          emit({
            type: 'CERTIFICATE_SELECTED',
            certificateId: String(payload.certificate_id ?? ''),
            sessionId: sessionId ?? '',
          });
          if (!pinSubmitted) {
            try {
              const pin = await resolvePin();
              pinSubmitted = true;
              send('PIN_SUBMIT', { pin });
            } catch (error) {
              settleReject(error instanceof Error ? error : new Error('PIN request failed'));
            }
          }
          break;
        }
        case 'PIN_ACCEPTED': {
          emit({ type: 'PIN_ACCEPTED', sessionId: sessionId ?? '' });
          break;
        }
        case 'QUEUE_WAIT': {
          emit({
            type: 'QUEUE_WAIT',
            tokenId: payload.token_id == null ? undefined : String(payload.token_id),
            position: Number(payload.position ?? 0),
            sessionId: sessionId ?? '',
          });
          break;
        }
        case 'SIGNING': {
          emit({
            type: 'SIGNING',
            phase: String(payload.phase ?? 'pkcs11'),
            sessionId: sessionId ?? '',
          });
          break;
        }
        case 'PIN_RETRY': {
          const reason = String(payload.reason ?? 'wrong_pin');
          emit({ type: 'PIN_RETRY', reason, sessionId: sessionId ?? '' });
          if (isAgentMode) {
            // Status-only — native Agent UI remains authoritative; do not call onPinRetry.
            break;
          }
          pinSubmitted = false;
          try {
            const pin = await resolvePin(reason);
            pinSubmitted = true;
            send('PIN_SUBMIT', { pin });
          } catch (error) {
            settleReject(error instanceof Error ? error : new Error('PIN retry failed'));
          }
          break;
        }
        case 'LOGIN_SUCCESS': {
          const challengeId = String(payload.challenge_id ?? challenge.challenge_id);
          emit({
            type: 'LOGIN_SUCCESS',
            challengeId,
            status: String(payload.status ?? 'success'),
            sessionId: sessionId ?? '',
          });
          settleResolve({
            success: true,
            challengeId,
            status: 'login_completed',
          });
          break;
        }
        case 'LOGIN_FAILED': {
          const code = String(payload.code ?? 'login_failed');
          const message = String(payload.message ?? 'login failed');
          const challengeId =
            payload.challenge_id == null ? undefined : String(payload.challenge_id);
          const backendCode =
            typeof payload.backend_code === 'number' ? payload.backend_code : undefined;
          emit({
            type: 'LOGIN_FAILED',
            code,
            message,
            challengeId,
            backendCode,
            sessionId: sessionId ?? undefined,
          });
          settleReject(new LoginFailedError(code, message, { challengeId, backendCode }));
          break;
        }
        case 'ERROR': {
          const code = String(payload.code ?? 'error');
          const message = String(payload.message ?? 'agent error');
          emit({
            type: 'ERROR',
            code,
            message,
            sessionId: sessionId ?? undefined,
          });
          settleReject(new LoginFailedError(code, message, { challengeId: challenge.challenge_id }));
          break;
        }
        case 'PONG':
          break;
        default:
          // Ignore unknown non-terminal types for forward compatibility.
          break;
      }
    };

    try {
      socket = createWebSocket ? createWebSocket(wsLoginUrl) : new WebSocketImpl(wsLoginUrl);
    } catch (error) {
      reject(error instanceof Error ? error : new Error('WebSocket construction failed'));
      return;
    }

    attachSocketHandlers(socket as SocketEventTarget, {
      open: () => {
        send('LOGIN_START', {
          agent_ticket: challenge.agent_ticket,
          challenge_id: challenge.challenge_id,
          nonce: challenge.nonce,
          purpose: challenge.purpose || 'login',
          payload_hash: challenge.payload_hash,
          payload: options.payload ?? '',
          pin_mode: pinMode,
        });
      },
      message: (data) => {
        void handleMessage(data);
      },
      error: () => {
        settleReject(
          new LoginFailedError('ws_error', 'WebSocket connection error', {
            challengeId: challenge.challenge_id,
          }),
        );
      },
      close: (ev) => {
        if (settled) {
          return;
        }
        settleReject(
          new LoginFailedError(
            'ws_closed',
            ev.reason || 'WebSocket closed before login completed',
            { challengeId: challenge.challenge_id },
          ),
        );
      },
    });
  });
}
